/**
 * Data Services Module
 * Responsible for data preparation, cleaning, transformation, and analysis
 */

class DataServices {
  /**
   * Process raw station data from API
   * @param {Array} rawStations - Raw station data from API
   * @returns {Array} Processed station data
   */
  processStationData(rawStations) {
    if (!Array.isArray(rawStations)) {
      console.warn('Invalid station data format');
      return [];
    }

    return rawStations.map(station => ({
      ...station,
      status: this.getStationStatus(station),
      lastSample: this.getLastSampleDate(station),
      averageResult: this.calculateAverageResult(station),
      dataQuality: this.assessDataQuality(station),
    }));
  }

  /**
   * Determine station status based on recent data
   * @param {Object} station - Station object with data
   * @returns {string} Status: 'active', 'maintenance', or 'inactive'
   */
  getStationStatus(station) {
    if (!station.data || station.data.length === 0) {
      return 'inactive';
    }

    const latestDate = new Date(station.data[0].sampledate);
    const daysSinceLastSample = (new Date() - latestDate) / (1000 * 60 * 60 * 24);

    if (daysSinceLastSample < 30) return 'active';
    if (daysSinceLastSample < 90) return 'maintenance';
    return 'inactive';
  }

  /**
   * Get the most recent sample date for a station
   * @param {Object} station - Station object
   * @returns {Date|null} Most recent sample date
   */
  getLastSampleDate(station) {
    if (!station.data || station.data.length === 0) {
      return null;
    }
    return new Date(station.data[0].sampledate);
  }

  /**
   * Calculate average result value for a station
   * @param {Object} station - Station object
   * @returns {number|null} Average result value
   */
  calculateAverageResult(station) {
    if (!station.data || station.data.length === 0) {
      return null;
    }

    const validResults = station.data
      .map(d => d.result)
      .filter(r => r !== null && r !== undefined && !isNaN(r));

    if (validResults.length === 0) {
      return null;
    }

    return validResults.reduce((sum, val) => sum + val, 0) / validResults.length;
  }

  /**
   * Assess data quality for a station
   * @param {Object} station - Station object
   * @returns {Object} Data quality metrics
   */
  assessDataQuality(station) {
    if (!station.data || station.data.length === 0) {
      return {
        completeness: 0,
        consistency: 0,
        timeliness: 0,
        overall: 'poor',
      };
    }

    // Calculate completeness (percentage of non-null results)
    const totalSamples = station.data.length;
    const validSamples = station.data.filter(d => d.result !== null).length;
    const completeness = (validSamples / totalSamples) * 100;

    // Calculate consistency (coefficient of variation)
    const results = station.data.map(d => d.result).filter(r => r !== null);
    const mean = results.reduce((a, b) => a + b, 0) / results.length;
    const variance = results.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / results.length;
    const stdDev = Math.sqrt(variance);
    const consistency = mean !== 0 ? 100 - (stdDev / mean) * 100 : 0;

    // Calculate timeliness
    const lastSample = new Date(station.data[0].sampledate);
    const daysSinceLastSample = (new Date() - lastSample) / (1000 * 60 * 60 * 24);
    const timeliness = Math.max(0, 100 - daysSinceLastSample);

    // Calculate overall quality
    const overallScore = (completeness + Math.max(0, consistency) + timeliness) / 3;
    let overall = 'poor';
    if (overallScore >= 80) overall = 'excellent';
    else if (overallScore >= 60) overall = 'good';
    else if (overallScore >= 40) overall = 'fair';

    return {
      completeness: Math.round(completeness),
      consistency: Math.round(Math.max(0, consistency)),
      timeliness: Math.round(timeliness),
      overall,
    };
  }

  /**
   * Generate time series data for charts
   * @param {Object} station - Station object
   * @param {number} limit - Maximum number of points
   * @returns {Array} Time series data
   */
  generateTimeSeriesData(station, limit = 20) {
    if (!station || !station.data) return [];

    return station.data
      .slice(0, limit)
      .reverse()
      .map(d => ({
        date: new Date(d.sampledate),
        value: d.result || 0,
        formatted: new Date(d.sampledate).toLocaleDateString(),
      }));
  }

  /**
   * Calculate statistical summary for station data
   * @param {Object} station - Station object
   * @returns {Object} Statistical summary
   */
  calculateStatistics(station) {
    if (!station || !station.data || station.data.length === 0) {
      return {
        count: 0,
        mean: null,
        median: null,
        min: null,
        max: null,
        stdDev: null,
        percentiles: {},
      };
    }

    const results = station.data
      .map(d => d.result)
      .filter(r => r !== null && !isNaN(r))
      .sort((a, b) => a - b);

    if (results.length === 0) {
      return {
        count: 0,
        mean: null,
        median: null,
        min: null,
        max: null,
        stdDev: null,
        percentiles: {},
      };
    }

    const count = results.length;
    const mean = results.reduce((a, b) => a + b, 0) / count;
    const median = count % 2 === 0
      ? (results[count / 2 - 1] + results[count / 2]) / 2
      : results[Math.floor(count / 2)];
    const min = results[0];
    const max = results[count - 1];

    // Calculate standard deviation
    const variance = results.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / count;
    const stdDev = Math.sqrt(variance);

    // Calculate percentiles
    const percentiles = {
      p25: this.getPercentile(results, 25),
      p50: median,
      p75: this.getPercentile(results, 75),
      p90: this.getPercentile(results, 90),
      p95: this.getPercentile(results, 95),
    };

    return {
      count,
      mean: Math.round(mean * 100) / 100,
      median: Math.round(median * 100) / 100,
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      stdDev: Math.round(stdDev * 100) / 100,
      percentiles,
    };
  }

  /**
   * Calculate percentile value
   * @param {Array} sortedArray - Sorted array of numbers
   * @param {number} percentile - Percentile to calculate (0-100)
   * @returns {number} Percentile value
   */
  getPercentile(sortedArray, percentile) {
    const index = (percentile / 100) * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (lower === upper) {
      return sortedArray[lower];
    }

    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  }

  /**
   * Group stations by region or other criteria
   * @param {Array} stations - Array of stations
   * @param {string} groupBy - Field to group by
   * @returns {Object} Grouped stations
   */
  groupStations(stations, groupBy = 'region') {
    return stations.reduce((groups, station) => {
      const key = station[groupBy] || 'Unknown';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(station);
      return groups;
    }, {});
  }

  /**
   * Filter stations based on criteria
   * @param {Array} stations - Array of stations
   * @param {Object} filters - Filter criteria
   * @returns {Array} Filtered stations
   */
  filterStations(stations, filters = {}) {
    return stations.filter(station => {
      if (filters.status && this.getStationStatus(station) !== filters.status) {
        return false;
      }

      if (filters.minSamples && (!station.data || station.data.length < filters.minSamples)) {
        return false;
      }

      if (filters.region && station.region !== filters.region) {
        return false;
      }

      if (filters.dateRange) {
        const lastSample = this.getLastSampleDate(station);
        if (!lastSample) return false;
        
        const { start, end } = filters.dateRange;
        if (start && lastSample < new Date(start)) return false;
        if (end && lastSample > new Date(end)) return false;
      }

      return true;
    });
  }

  /**
   * Prepare data for export
   * @param {Array} stations - Array of stations
   * @param {Object} options - Export options
   * @returns {Array} Flattened data for export
   */
  prepareExportData(stations, options = {}) {
    const exportData = [];

    stations.forEach(station => {
      if (options.flatten && station.data) {
        // Flatten station data with samples
        station.data.forEach(sample => {
          exportData.push({
            stationId: station.stationid,
            latitude: station.latitude,
            longitude: station.longitude,
            status: this.getStationStatus(station),
            sampleDate: sample.sampledate,
            result: sample.result,
          });
        });
      } else {
        // Export station summary
        exportData.push({
          stationId: station.stationid,
          latitude: station.latitude,
          longitude: station.longitude,
          status: this.getStationStatus(station),
          sampleCount: station.data?.length || 0,
          lastSample: this.getLastSampleDate(station),
          averageResult: this.calculateAverageResult(station),
        });
      }
    });

    return exportData;
  }

  /**
   * Sort station data by sampledate
   * @param {Array} data - Array of data objects with sampledate property
   * @param {string} order - Sort order: 'asc' for ascending (oldest first) or 'desc' for descending (newest first)
   * @returns {Array} Sorted data array
   */
  sortBySampleDate(data, order = 'asc') {
    if (!Array.isArray(data) || data.length === 0) {
      return data;
    }

    return [...data].sort((a, b) => {
      const dateA = new Date(a.sampledate || a.sampleDate);
      const dateB = new Date(b.sampledate || b.sampleDate);
      
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      if (order === 'asc') {
        return dateA - dateB; // Oldest first
      } else {
        return dateB - dateA; // Newest first
      }
    });
  }

  /**
   * Prepare station data for display (with proper sorting)
   * @param {Object} station - Station object with data array
   * @returns {Array} Sorted data array for display
   */
  prepareStationDataForDisplay(station) {
    if (!station || !station.data || !Array.isArray(station.data)) {
      return [];
    }

    // Create display data with proper formatting
    const displayData = station.data.map((item, index) => ({
      id: index + 1,
      sampledate: item.sampledate,
      result: item.result,
      originalResult: item.result // Keep original for comparison
    }));

    // Sort by sampledate ascending (oldest to newest) for chronological display
    return this.sortBySampleDate(displayData, 'asc');
  }
}

// Export singleton instance
const dataServices = new DataServices();
export default dataServices;

// Export bound individual methods for convenience
export const processStationData = dataServices.processStationData.bind(dataServices);
export const getStationStatus = dataServices.getStationStatus.bind(dataServices);
export const getLastSampleDate = dataServices.getLastSampleDate.bind(dataServices);
export const calculateAverageResult = dataServices.calculateAverageResult.bind(dataServices);
export const assessDataQuality = dataServices.assessDataQuality.bind(dataServices);
export const generateTimeSeriesData = dataServices.generateTimeSeriesData.bind(dataServices);
export const calculateStatistics = dataServices.calculateStatistics.bind(dataServices);
export const groupStations = dataServices.groupStations.bind(dataServices);
export const filterStations = dataServices.filterStations.bind(dataServices);
export const prepareExportData = dataServices.prepareExportData.bind(dataServices);